import Lottie from 'lottie-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState, type JSX } from 'react';
import codAnimation from '@/assets/animations/COD.json';
import slaydTextIcon from '@/assets/svg/static/slaydText.svg';
import { DoubleColumnProductList } from '@/components/features';
import { EVENTS, PROPERTIES, trackEvent } from '@/services/posthog';
import { fetchCurationById } from '@/services/slaydAPI';
import useLayoutStore from '@/stores/useLayoutStore';
import type { Product } from '@/types/Product.types';
import { capitalizeFirstLetter, decodeBase64Url } from '@/utils/formatting';
import styles from './CuratorStorePage.module.css';

export default function CuratorStorePage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const rawCuratorName = searchParams.get('curatorName');
  const curatorName = rawCuratorName
    ? capitalizeFirstLetter(rawCuratorName)
    : '';
  const curationId = searchParams.get('curation_id');
  const keyParam = searchParams.get('key');

  let rawInstagramHandle: string | null = null;
  if (keyParam) {
    try {
      const decodedStr = decodeBase64Url(keyParam);
      const parts = decodedStr.trim().split('|');
      if (parts[1]) rawInstagramHandle = parts[1];
    } catch (err) {
      console.error('Error decoding curator key:', err);
    }
  }

  const instagramHandle = rawInstagramHandle || curatorName;
  const navigate = useNavigate();

  const setNavbarVisibility = useLayoutStore(
    (state) => state.setNavbarVisibility
  );

  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const fetchingData = useRef<boolean>(false);

  useEffect(() => {
    setNavbarVisibility(false); // Hide bottom tabs
    const isValid = curationId && curatorName;

    trackEvent(EVENTS.CURATOR_STORE_VALIDATION, {
      [PROPERTIES.CURATION_ID]: curationId || '',
      [PROPERTIES.CURATOR_NAME]: curatorName || '',
      validation_passed: !!isValid,
    });

    if (!isValid) {
      setHasError(true);
    }
  }, [curationId, curatorName, setNavbarVisibility]);

  useEffect(() => {
    if (!curationId || !curatorName) return;

    const loadCuration = async (): Promise<void> => {
      if (fetchingData.current) return;
      fetchingData.current = true;
      setIsLoading(true);
      setHasError(false);

      try {
        const data = await fetchCurationById(curationId);
        if (data && data.products) {
          setProducts(data.products);
          trackEvent(EVENTS.CURATOR_STORE_VIEW, {
            [PROPERTIES.CURATOR_NAME]: curatorName,
            [PROPERTIES.CURATION_ID]: curationId,
            [PROPERTIES.ITEM_COUNT]: data.products.length,
            [PROPERTIES.SOURCE]: 'curator_store',
          });
        } else {
          setHasError(true);
          trackEvent(EVENTS.CURATOR_STORE_LOAD_FAILED, {
            [PROPERTIES.CURATION_ID]: curationId,
            [PROPERTIES.FAILURE_REASON]: 'empty_response',
            [PROPERTIES.SOURCE]: 'curator_store',
          });
        }
      } catch (error) {
        console.error('Failed to fetch curation for curator store:', error);
        trackEvent(EVENTS.CURATOR_STORE_LOAD_FAILED, {
          [PROPERTIES.CURATION_ID]: curationId,
          [PROPERTIES.FAILURE_REASON]:
            error instanceof Error ? error.message : String(error),
          [PROPERTIES.SOURCE]: 'curator_store',
        });
        setHasError(true);
      } finally {
        fetchingData.current = false;
        setIsLoading(false);
      }
    };

    loadCuration();
  }, [curationId, curatorName]);

  const handleErrorCTAClick = (): void => {
    trackEvent(EVENTS.CURATOR_STORE_ERROR_CTA_CLICK, {
      [PROPERTIES.CURATOR_NAME]: curatorName || '',
      [PROPERTIES.CURATION_ID]: curationId || '',
      [PROPERTIES.SOURCE]: 'curator_store',
    });
    navigate('/');
  };

  if (hasError) {
    return (
      <div className={styles.container}>
        <div className={styles.headerArea}>
          <h1 className={styles.originalsTitle}>
            <img
              src={slaydTextIcon}
              alt="Slayd"
              className={styles.slaydTextLogo}
            />
            {curatorName && (
              <>
                <span className={styles.xText}>x</span>
                <span className={styles.originalsTitleCursive}>
                  {instagramHandle}
                </span>
              </>
            )}
          </h1>
        </div>
        <div className={styles.errorContainer}>
          <h2 className={styles.errorTitle}>Storefront Not Found</h2>
          <p className={styles.errorMessage}>
            {
              "We couldn't load this curator's collection. It might be temporarily unavailable or the link might be incorrect."
            }
          </p>
          <button className={styles.errorButton} onClick={handleErrorCTAClick}>
            Explore Slayd Originals
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerArea}>
        <h1 className={styles.originalsTitle}>
          <img
            src={slaydTextIcon}
            alt="Slayd"
            className={styles.slaydTextLogo}
          />
          <span className={styles.xText}>x</span>
          <span className={styles.originalsTitleCursive}>
            {instagramHandle}
          </span>
        </h1>
        <span className={styles.originalsSubtitle}>
          Trusted by 1lakh+ customers
        </span>
        <div className={styles.codBannerWrapper}>
          <div className={styles.codBanner}>
            <Lottie
              autoplay
              animationData={codAnimation}
              className={styles.codLottie}
              loop={true}
            />
          </div>
        </div>
      </div>

      <div className={styles.productListWrapper}>
        <DoubleColumnProductList
          products={products}
          hasMore={false}
          isLoading={isLoading}
          additionalFieldsTracking={{
            [PROPERTIES.CURATOR_NAME]: curatorName,
            [PROPERTIES.CURATION_ID]: curationId || '',
          }}
          onLoadMore={() => {}}
        />
      </div>
    </div>
  );
}
